import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtLakh, fmtDate, BUCKET_META } from "../lib/format";
import CircuitTable from "../components/CircuitTable";
import RechargeModal from "../components/RechargeModal";
import CancelModal from "../components/CancelModal";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, CheckCircle2, Database, CalendarDays, ArrowRight, ShieldCheck, Zap } from "lucide-react";

const DISC_META = {
  link_inactive_billed: { label: "Closed/suspended but billed", cls: "bg-rose-50 border-rose-300" },
  mbps_mismatch: { label: "Mbps mismatch", cls: "bg-amber-50 border-amber-300" },
  isp_mismatch: { label: "ISP mismatch", cls: "bg-amber-50 border-amber-300" },
  cross_vendor_double_bill: { label: "Double-billed (same service)", cls: "bg-rose-50 border-rose-300" },
  too_many_in_cycle: { label: "Too many in cycle", cls: "bg-amber-50 border-amber-300" },
  orphan_bill: { label: "Couldn't place", cls: "bg-slate-50 border-slate-200" },
};

async function downloadCsv(url, filename) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objUrl; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch { /* ignore */ }
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState([]);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState("overdue");
  const [svc, setSvc] = useState("");
  const [recharge, setRecharge] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [sync, setSync] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [discrep, setDiscrep] = useState(null);
  const [chase, setChase] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [bb, setBb] = useState(null);
  const [bbRisk, setBbRisk] = useState(null);
  const [aiq, setAiq] = useState(null);

  const load = useCallback(async () => {
    const [k, s] = await Promise.all([
      api.get(`/dashboard/kpis${svc ? `?service_type=${svc}` : ""}`),
      api.get(`/dashboard/summary`),
    ]);
    setKpis(k.data.buckets);
    setSummary(s.data);
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/sync/lastmile/report").then((r) => setSync(r.data)).catch(() => {});
    api.get("/dashboard/forecast").then((r) => setForecast(r.data)).catch(() => {});
    api.get("/dashboard/discrepancies").then((r) => setDiscrep(r.data)).catch(() => {});
    api.get("/dashboard/chase-list").then((r) => setChase(r.data)).catch(() => {});
    api.get("/dashboard/unbilled").then((r) => setUnbilled(r.data)).catch(() => {});
    api.get("/dashboard/broadband-discipline").then((r) => setBb(r.data)).catch(() => {});
    api.get("/dashboard/backbone-risk").then((r) => setBbRisk(r.data)).catch(() => {});
    api.get("/vendor-invoices/ai/questions-count").then((r) => setAiq(r.data)).catch(() => {});
  }, [svc]);

  useEffect(() => { load(); }, [load, refresh]);

  const totalOverdueToday = kpis.filter((b) => ["overdue", "today"].includes(b.key)).reduce((a, b) => a + b.count, 0);
  const done = () => { setRefresh((r) => r + 1); };

  const dqLabels = { expiry_missing: "Expiry Missing", cycle_missing: "Cycle Missing", amount_missing: "Amount Missing", isp_id_missing: "ISP ID Missing", assigned_missing: "Unassigned", location_missing: "No Location" };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-slate-900">Broadband Renewal Control</h1>
          <p className="text-slate-500 text-sm mt-1">Good morning, {user?.name?.split(" ")[0]} — here is everything that needs action today.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg text-sm">
          {[["", "All"], ["Broadband", "Broadband"], ["ILL", "ILL"]].map(([v, l]) => (
            <button key={v} onClick={() => setSvc(v)} data-testid={`svc-filter-${l}`}
              className={`px-3 py-1.5 rounded-md font-medium ${svc === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {totalOverdueToday > 0 && (
        <div className="flex items-center gap-3 bg-red-600 text-white rounded-xl px-4 py-3 shadow-sm" data-testid="urgency-banner">
          <AlertTriangle className="shrink-0" />
          <div className="text-sm font-medium">
            <b>{totalOverdueToday} connection(s)</b> are overdue or expiring today. Recharge them now to prevent disconnection.
          </div>
        </div>
      )}

      {/* Critical-failure alerts (backup / offsite / AI key / sync / spend) */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="alerts-banner">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle size={16} /> {alerts.length} active alert(s)</div>
          <ul className="mt-1 space-y-0.5">
            {alerts.slice(0, 6).map((a) => (
              <li key={a.id} className="text-sm text-amber-800"><b className="uppercase text-[10px] mr-1">{a.code}</b>{a.message}{a.count > 1 ? ` (×${a.count})` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {/* AI questions waiting for a person (ask-when-unsure) */}
      {aiq?.waiting > 0 && (
        <button onClick={() => nav("/invoices")} className="w-full text-left rounded-xl border border-indigo-300 bg-indigo-50 px-4 py-3 hover:border-indigo-400" data-testid="ai-questions-banner">
          <div className="flex items-center gap-2 text-indigo-800 font-semibold text-sm">
            <ShieldCheck size={16} /> {aiq.waiting} AI question{aiq.waiting === 1 ? "" : "s"} waiting for your decision
          </div>
          <div className="text-xs text-indigo-700 mt-0.5">The AI flagged bills it's unsure about (bill type, which link, cycle change). Open Invoices to answer — nothing is matched or paid until you click.</div>
        </button>
      )}

      {/* LastMile sync + 90-day cash-out forecast */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><Database size={16} /> LastMile sync</div>
          {sync?.ran_at ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Last run {fmtDate(sync.ran_at)} · {sync.ok ? <span className="text-emerald-600">OK</span> : <span className="text-red-600">failed</span>}</div>
              <div className="text-xs text-slate-500">created {sync.created || 0} · updated {sync.updated || 0} · suspended {sync.suspended || 0}{(sync.errors || []).length ? ` · ${sync.errors.length} error(s)` : ""}</div>
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">No sync has run yet.</div>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><CalendarDays size={16} /> 90-day cash-out</div>
          {forecast ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Total expected outflow: <b>{fmtLakh(forecast.total)}</b> across {forecast.by_vendor?.length || 0} vendor(s)</div>
              <div className="text-xs text-slate-500">{(forecast.by_vendor || []).slice(0, 4).map((v) => `${v.vendor}: ${fmtINR(v.amount)}`).join(" · ")}</div>
              {forecast.cycle_change_bumps?.length > 0 && <div className="text-xs text-amber-700">⚑ {forecast.cycle_change_bumps.length} cycle-change bump(s) in the window</div>}
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">No forecast yet.</div>}
        </div>
      </div>

      {/* ILL finance controls — discrepancies, chase list, unbilled liability */}
      {discrep?.tiles?.some((t) => t.count > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {discrep.tiles.filter((t) => t.count > 0).map((t) => (
            <button key={t.code} onClick={() => nav(`/invoices`)}
              className={`text-left rounded-xl border p-4 ${DISC_META[t.code]?.cls || "bg-slate-50 border-slate-200"}`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{DISC_META[t.code]?.label || t.code}</div>
              <div className="font-head text-3xl font-extrabold mt-2 text-slate-900">{t.count}</div>
              {t.impact_inr > 0 && <div className="font-mono-num text-sm font-semibold mt-1 text-rose-700">{fmtINR(t.impact_inr)} at risk</div>}
            </button>
          ))}
        </div>
      )}

      {/* Backbone chain risk — a DC/feed bill overdue strands POPs + customers downstream */}
      {bbRisk?.tiles?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800 mb-2">
            <Zap size={16} className="text-rose-600" /> Backbone chain risk
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {bbRisk.tiles.map((t) => (
              <button key={t.billable_id} onClick={() => nav(`/invoices`)}
                className="text-left rounded-xl border border-rose-300 bg-rose-50 p-4 hover:border-rose-400">
                <div className="text-sm font-bold text-slate-900">
                  {t.vendor} bill overdue
                  <span className="ml-2 text-[11px] uppercase tracking-wide text-slate-500">{t.kind === "dc" ? "DC upstream" : "POP feed"}</span>
                </div>
                <div className="font-head text-2xl font-extrabold mt-2 text-rose-700">
                  {t.pops_at_risk} POPs · {t.customers_at_risk} customers at risk
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {t.circuit_id ? `Circuit ${t.circuit_id} · ` : ""}{t.days_overdue}d overdue
                  {t.pops_reduced > 0 ? ` · ${t.pops_reduced} POP(s) reduced (paid backup)` : ""}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Missing-invoice chase list */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><AlertTriangle size={16} /> Missing-invoice chase</div>
            <button onClick={() => downloadCsv("/dashboard/chase-list.csv", "missing-invoices.csv")} className="text-xs text-blue-700 font-medium">Export CSV</button>
          </div>
          {chase ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div><b>{chase.chase?.length || 0}</b> bill(s) to chase · <span className="text-slate-500">{chase.received_not_placed?.length || 0} received, not placed</span></div>
              <div className="flex gap-2 mt-1 flex-wrap">
                {Object.entries(chase.aging || {}).map(([k, v]) => (
                  <span key={k} className={`text-xs px-2 py-0.5 rounded ${v > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-400"}`}>{k}d: {v}</span>
                ))}
              </div>
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">—</div>}
        </div>

        {/* Unbilled liability + couldn't place */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><Database size={16} /> Unbilled liability</div>
          {unbilled ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Owed, not yet invoiced: <b>{fmtLakh(unbilled.total)}</b></div>
              <div className="text-xs text-slate-500">{(unbilled.by_isp || []).slice(0, 3).map((v) => `${v.vendor}: ${fmtINR(v.amount)}`).join(" · ") || "—"}</div>
              <div className="text-xs text-rose-700 mt-1">Received, couldn't place: <b>{unbilled.couldnt_place?.count || 0}</b> bill(s) · {fmtINR(unbilled.couldnt_place?.amount || 0)}</div>
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">—</div>}
        </div>

        {/* Broadband prepaid upload discipline */}
        <button onClick={() => nav("/action-required?service_type=Broadband")} className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-amber-300">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><CheckCircle2 size={16} /> Broadband upload discipline</div>
          {bb ? (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {[["due_soon_not_uploaded", "Due soon", "bg-amber-100 text-amber-800"],
                ["overdue_not_uploaded", "Overdue", "bg-orange-100 text-orange-800"],
                ["never_uploaded", "Never uploaded", "bg-rose-100 text-rose-700"],
                ["recharge_no_receipt", "Receipt missing", "bg-slate-100 text-slate-600"]].map(([k, lbl, cls]) => (
                <span key={k} className={`px-2 py-0.5 rounded ${cls}`}>{lbl}: <b>{bb.counts?.[k] || 0}</b></span>
              ))}
              {bb.counts?.paid_for_inactive > 0 && (
                <span className="px-2 py-0.5 rounded bg-rose-600 text-white">⚑ Paid for inactive: <b>{bb.counts.paid_for_inactive}</b> · {fmtINR(bb.inactive_paid_inr)}</span>
              )}
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">—</div>}
        </button>
      </div>

      {/* Renewal Coverage KPI */}
      {summary?.renewal_coverage && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="coverage-kpi">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-6">
            <div className="relative shrink-0">
              <svg width="96" height="96" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="40" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                <circle cx="48" cy="48" r="40" fill="none" stroke={summary.renewal_coverage.broadband_coverage_pct >= 98 ? "#16A34A" : summary.renewal_coverage.broadband_coverage_pct >= 90 ? "#2563EB" : "#EA580C"}
                  strokeWidth="10" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - summary.renewal_coverage.broadband_coverage_pct / 100)}`}
                  transform="rotate(-90 48 48)" style={{ transition: "stroke-dashoffset .6s ease" }} />
                <text x="48" y="52" textAnchor="middle" className="font-head" fontSize="20" fontWeight="800" fill="#0F172A">{summary.renewal_coverage.broadband_coverage_pct}%</text>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-800 font-head font-bold"><ShieldCheck size={18} className="text-emerald-600" /> Renewal Coverage — Broadband</div>
              <p className="text-sm text-slate-500 mt-1">Active broadband circuits under automatic renewal control.</p>
              <div className="flex gap-6 mt-3 text-sm">
                <div><div className="font-head text-2xl font-extrabold">{summary.renewal_coverage.broadband_active}</div><div className="text-xs text-slate-500">Active Broadband</div></div>
                <div><div className="font-head text-2xl font-extrabold text-emerald-600">{summary.renewal_coverage.broadband_controlled}</div><div className="text-xs text-slate-500">Under Control</div></div>
                <div><div className="font-head text-2xl font-extrabold text-orange-600">{summary.renewal_coverage.broadband_incomplete}</div><div className="text-xs text-slate-500">Incomplete</div></div>
              </div>
            </div>
          </div>
          <button onClick={() => nav("/data-quality?view=incomplete")} data-testid="renewal-incomplete-exception"
            className={`text-left rounded-xl border p-5 transition-colors ${summary.renewal_coverage.incomplete > 0 ? "bg-orange-50 border-orange-300 hover:border-orange-400" : "bg-emerald-50 border-emerald-300"}`}>
            <div className="flex items-center gap-2 font-head font-bold text-orange-700"><AlertTriangle size={18} /> Renewal Data Incomplete</div>
            <div className="font-head text-4xl font-extrabold text-orange-600 mt-3">{summary.renewal_coverage.incomplete}</div>
            <p className="text-sm text-slate-600 mt-1">Active circuits NOT under renewal control. Click to fix them →</p>
          </button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {kpis.map((b) => {
          const meta = BUCKET_META[b.key];
          return (
            <button key={b.key} data-testid={`kpi-card-${b.key}`} onClick={() => setActive(b.key)}
              className={`kpi-card text-left rounded-xl border p-4 ${meta.card} ${active === b.key ? "active" : ""}`}>
              <div className={`text-[11px] font-bold uppercase tracking-wide ${meta.text}`}>{meta.label}</div>
              <div className={`font-head text-3xl font-extrabold mt-2 ${meta.num}`}>{b.count}</div>
              <div className="text-xs text-slate-500 mt-0.5">connections</div>
              <div className={`font-mono-num text-sm font-semibold mt-2 ${meta.text}`}>{fmtINR(b.amount)}</div>
            </button>
          );
        })}
      </div>

      {/* Action Required table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head text-lg font-bold flex items-center gap-2">
            Action Required
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${BUCKET_META[active]?.badge}`}>{BUCKET_META[active]?.label}</span>
          </h2>
          <button onClick={() => nav("/action-required")} className="text-sm text-blue-700 font-medium flex items-center gap-1 hover:gap-2 transition-all">Full view <ArrowRight size={15} /></button>
        </div>
        <CircuitTable baseFilters={{ bucket: active, service_type: svc, status: "Active" }} onRecharge={setRecharge} onCancel={setCancel} refreshKey={refresh} />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <button onClick={() => nav("/action-required?view=verify")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-300 transition-colors" data-testid="tile-verification">
          <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 size={18} /><span className="font-head font-bold">Recharge Verification Pending</span></div>
          <div className="font-head text-3xl font-extrabold mt-3">{summary?.verification_pending ?? "—"}</div>
          <p className="text-sm text-slate-500 mt-1">Recharged circuits awaiting internet/payment confirmation.</p>
        </button>
        <button onClick={() => nav("/calendar")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors" data-testid="tile-calendar">
          <div className="flex items-center gap-2 text-blue-600"><CalendarDays size={18} /><span className="font-head font-bold">Upcoming Renewal Calendar</span></div>
          <div className="font-head text-3xl font-extrabold mt-3">{kpis.reduce((a, b) => a + b.count, 0)}</div>
          <p className="text-sm text-slate-500 mt-1">Renewals due in the next 30 days. Open the calendar to plan cash.</p>
        </button>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="tile-dataquality">
          <div className="flex items-center gap-2 text-orange-600"><Database size={18} /><span className="font-head font-bold">Missing Information</span></div>
          <div className="flex flex-wrap gap-2 mt-3">
            {summary && Object.entries(summary.data_quality || {}).map(([k, v]) => (
              <button key={k} onClick={() => nav(`/data-quality?flag=${k}`)} className="text-xs px-2.5 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100">
                <b>{v}</b> {dqLabels[k] || k}
              </button>
            ))}
            {summary && Object.keys(summary.data_quality || {}).length === 0 && <span className="text-sm text-slate-400">All data complete 🎉</span>}
          </div>
        </div>
      </div>

      {recharge && <RechargeModal circuit={recharge} onClose={() => setRecharge(null)} onDone={done} />}
      {cancel && <CancelModal circuit={cancel} onClose={() => setCancel(null)} onDone={done} />}
    </div>
  );
}
